import { useEffect, useRef, useState } from 'react';
import { useDetectionEngine } from '../features/detection/useDetectionEngine';
import { TopBar } from '../components/TopBar';
import { RulesSidebar } from '../features/rules/RulesSidebar';
import { MapPane } from '../features/map/MapPane';
import { AlertsFeed } from '../features/alerts/AlertsFeed';
import { ResizeHandle } from '../features/layout/ResizeHandle';
import { usePersistentState } from '../features/layout/usePersistentState';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { InvestigationPanel } from '../features/map/InvestigationPanel';
import { InvestigationHoverProvider } from '../features/map/investigationHover';
import { closeInvestigation } from '../features/map/investigationSlice';
import {
  RULES_MIN,
  RULES_MAX,
  RULES_DEFAULT,
  ALERTS_MIN,
  ALERTS_MAX,
  ALERTS_DEFAULT,
  COLLAPSE_BREAKPOINT,
  HANDLE_WIDTH,
  LS_RULES_W,
  LS_ALERTS_W,
  LS_ALERTS_COLLAPSED,
} from '../features/layout/layoutConstants';

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

export default function Dashboard() {
  // Drive per-rule detection ticks; the engine owns all timers + alert state.
  useDetectionEngine();

  const dispatch = useAppDispatch();
  // Investigate Mode: hide both sidebars, show the investigation panel on the
  // right, keep the TopBar. Closing restores instantly (alerts stay in Redux).
  const investigating = useAppSelector((s) => Boolean(s.investigation.active));

  // Esc closes Investigate Mode.
  useEffect(() => {
    if (!investigating) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dispatch(closeInvestigation());
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [investigating, dispatch]);

  // Persisted, drag-resizable + collapsible sidebar layout.
  const [rulesW, setRulesW] = usePersistentState(LS_RULES_W, RULES_DEFAULT);
  const [alertsW, setAlertsW] = usePersistentState(LS_ALERTS_W, ALERTS_DEFAULT);
  const rulesCollapsed = false; // rules panel is always shown (collapse toggle removed for now)
  const [alertsCollapsed, setAlertsCollapsed] = usePersistentState(LS_ALERTS_COLLAPSED, false);

  const onRulesResize = (d: number) => setRulesW((w) => clamp(w + d, RULES_MIN, RULES_MAX));
  const onAlertsResize = (d: number) => setAlertsW((w) => clamp(w - d, ALERTS_MIN, ALERTS_MAX));

  // While a handle is being dragged we suppress the grid's column transition:
  // the easing would otherwise lag the pointer and keep the map's ResizeObserver
  // firing for 240ms after release, making the canvas wobble. The transition is
  // kept for collapse/expand, which *should* animate.
  const [dragging, setDragging] = useState(false);

  // Auto-collapse both sidebars on narrow viewports, but only when *crossing*
  // the breakpoint so it never fights a manual expand on every resize tick.
  const wasNarrow = useRef(false);
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${COLLAPSE_BREAKPOINT}px)`);
    const apply = (matches: boolean) => {
      if (matches && !wasNarrow.current) {
        setAlertsCollapsed(true);
      }
      wasNarrow.current = matches;
    };
    apply(mq.matches);
    const onChange = (e: MediaQueryListEvent) => apply(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Investigate Mode overrides the layout: rules + resize handles collapse to 0,
  // and the right column becomes the fixed-width investigation panel.
  const INVESTIGATE_PANEL_W = 380;
  const cols = investigating
    ? ['0px', '0px', '1fr', '0px', `${INVESTIGATE_PANEL_W}px`].join(' ')
    : [
        rulesCollapsed ? '0px' : `${rulesW}px`,
        rulesCollapsed ? '0px' : `${HANDLE_WIDTH}px`,
        '1fr',
        alertsCollapsed ? '0px' : `${HANDLE_WIDTH}px`,
        alertsCollapsed ? '0px' : `${alertsW}px`,
      ].join(' ');

  return (
    <div
      className="theme-noc"
      style={{
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
        background: 'var(--surface-app)',
        color: 'var(--text-primary)',
        display: 'grid',
        gridTemplateRows: '60px 1fr',
      }}
    >
      <TopBar />
      <InvestigationHoverProvider>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: cols,
            minHeight: 0,
            transition: dragging ? 'none' : 'grid-template-columns 240ms cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          {/* Left rules sidebar — hidden in Investigate Mode. */}
          {investigating ? <div /> : <RulesSidebar />}
          {!investigating && !rulesCollapsed ? (
            <ResizeHandle
              ariaLabel="Resize rules panel"
              onResize={onRulesResize}
              onResizeStart={() => setDragging(true)}
              onResizeEnd={() => setDragging(false)}
            />
          ) : (
            <div />
          )}
          <div style={{ position: 'relative', minWidth: 0, minHeight: 0, display: 'grid' }}>
            <MapPane />
            {!investigating && (
              <PanelToggle
                side="right"
                collapsed={alertsCollapsed}
                onClick={() => setAlertsCollapsed((c) => !c)}
                label={alertsCollapsed ? 'Show alerts panel' : 'Hide alerts panel'}
              />
            )}
          </div>
          {!investigating && !alertsCollapsed ? (
            <ResizeHandle
              ariaLabel="Resize alerts panel"
              onResize={onAlertsResize}
              onResizeStart={() => setDragging(true)}
              onResizeEnd={() => setDragging(false)}
            />
          ) : (
            <div />
          )}
          {/* Right column: the alerts feed normally, the investigation panel in Investigate Mode. */}
          {investigating ? <InvestigationPanel /> : <AlertsFeed />}
        </div>
      </InvestigationHoverProvider>
    </div>
  );
}

/** Small chevron button pinned to an inner edge of the map; collapses/expands a sidebar. */
function PanelToggle({
  side,
  collapsed,
  onClick,
  label,
}: {
  side: 'left' | 'right';
  collapsed: boolean;
  onClick: () => void;
  label: string;
}) {
  // Chevron points toward the map when expanded (to hide), away when collapsed (to reveal).
  const pointsLeft = side === 'left' ? !collapsed : collapsed;
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      style={{
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        [side]: 6,
        width: 22,
        height: 44,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        background: 'var(--surface-card)',
        border: '1px solid var(--border-hover)',
        borderRadius: 7,
        color: 'var(--text-muted)',
        cursor: 'pointer',
        zIndex: 5,
        boxShadow: '0 4px 14px rgba(0,0,0,0.35)',
      }}
    >
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        {pointsLeft ? <polyline points="15 18 9 12 15 6" /> : <polyline points="9 18 15 12 9 6" />}
      </svg>
    </button>
  );
}
